import { ChartOptions } from "chart.js";
import DonutChartJS from "./DonutChartJS";
import "./SpendAnalysis.scss";
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import BarWithAnnotation from "./BarWithAnnotation";
import {
	fetchSpendByCountry,
	fetchSpendBySupplier,
	fetchSpendByYear,
	SpendByCountry,
	SpendBySupplier,
	SpendByYear,
} from "../../../graphql/negotiations";
import { useParams } from "react-router-dom";
import { useToastStore } from "../../../stores/toastStore";
// @ts-ignore
import * as utils from "../../../utils/utils.js";
import { Tooltip } from "primereact/tooltip";
import useLoaderStore from "../../../stores/useLoaderStore.js";
const SpendAnalysis = () => {
	const { negotiation_id } = useParams<{ negotiation_id?: string }>();
	const negId = negotiation_id ? parseInt(negotiation_id, 10) : undefined;
	const [bySupplier, setBySupplier] = useState<SpendBySupplier[]>([]);
	const [byCountry, setByCountry] = useState<SpendByCountry[]>([]);
	const [byYear, setByYear] = useState<SpendByYear[]>([]);
	const { error } = useToastStore.getState();
    const { showLoader } = useLoaderStore.getState();

	useEffect(() => {
		let mounted = true;
		(async () => {
			try {
				showLoader(true);
				const [s, c, y] = await Promise.all([
					fetchSpendBySupplier(negId),
					fetchSpendByCountry(negId),
					fetchSpendByYear(negId),
				]);
				if (!mounted) return;
				const updatedBySupplier = s.map((item) => ({
					...item,
					total_cost_updated: utils.roundToTwo(item.total_cost),
					total_volume_updated: utils.roundToTwo(item.total_volume),
					avg_price: utils.formatCost(item.avg_price),
				}));
				const updatedByCountry = c.map((item) => ({
					...item,
					total_cost_updated: utils.formatCost(item.total_cost),
					total_volume_updated: utils.roundToTwo(item.total_volume),
					avg_price: utils.formatCost(item.avg_price),
				}));
				const updatedByYear = y.map((item) => ({
					...item,
					total_cost_updated: utils.formatCost(item.total_cost),
					total_volume_updated: utils.roundToTwo(item.total_volume),
					avg_price: utils.formatCost(item.avg_price),
				}));
				setBySupplier(updatedBySupplier);
				setByCountry(updatedByCountry);
				setByYear(updatedByYear);
			} catch (e: any) {
				if (!mounted) return;
				error("Error", `Failed to fetch analytics: ${e.message}`);
			} finally {
				if (mounted) showLoader(false);
			}
		})();
		return () => {
			mounted = false;
		};
	}, [negId]);

	const PALETTE = [
		"#5EC4F0",
		"#0D3D8F",
		"#FF7F0E",
		"#F9C80E",
		"#06B6D4",
		"#84CC16",
		"#EC4899",
		"#8B5CF6",
	];
	const pickColor = (i: number) => PALETTE[i % PALETTE.length];

	// Supplier donut
	const supplierDonut = useMemo(() => {
		const labels = bySupplier.map((s) => s.supplier_name ?? "Unknown");
		const values = bySupplier.map((s) => s.total_cost ?? 0);
		return {
			data: {
				labels,
				datasets: [
					{
						data: values,
						backgroundColor: values.map((_, i) => pickColor(i)),
						borderColor: "var(--surface-default)",
						borderWidth: 1,
						hoverOffset: 0,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: "74%",
				rotation: -90,
				circumference: 360,
				plugins: {
					legend: { display: false },
					datalabels: {
						color: "#000",
						formatter: (value: number) => utils.formatCost(value),
					},
				},
			} as ChartOptions<"doughnut">,
		};
	}, [bySupplier]);

	// Country donut
	const countryDonut = useMemo(() => {
		const labels = byCountry.map((x) => x.country ?? "Unknown");
		const values = byCountry.map((x) => x.total_cost ?? 0);

		return {
			data: {
				labels,
				datasets: [
					{
						data: values,
						backgroundColor: values.map((_, i) => pickColor(i)),
						borderColor: "var(--surface-default)",
						borderWidth: 1,
						hoverOffset: 0,
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: "74%",
				rotation: -90,
				circumference: 360,
				plugins: {
					legend: { display: false },
					datalabels: {
						color: "#000",
						formatter: (value: number) => utils.formatCost(value),
					},
				},
			} as ChartOptions<"doughnut">,
		};
	}, [byCountry]);

	// Year series (use BarWithAnnotation)
	const yearLabels = useMemo(
		() => byYear.map((y) => String(y.year)),
		[byYear]
	);
	const yearValues = useMemo(
		() => byYear.map((y) => y.total_cost ?? 0),
		[byYear]
	);

	return (
		<div className="spend-analysis">
			{/* first card */}
			<div className="cards">
				<div className="title">
					CPV by Supplier (€)
					{/* info and tooltip */}
					<span className="info-icon">
						<Tooltip target=".supplier-info-icon" />
						<i
							data-pr-tooltip="The total cost of all bids submitted by each supplier"
							data-pr-position="right"
							data-pr-at="right+5 top"
							data-pr-my="left center-2"
							className="supplier-info-icon pi pi-info-circle"
							style={{ fontSize: "1rem" }}
						></i>
					</span>
				</div>
				<div className="chart" style={{ width: "100%" }}>
					<DonutChartJS
						data={supplierDonut.data}
						options={supplierDonut.options}
					/>
				</div>
				{/* table */}
				<div className="analysis-table w-full">
					<DataTable
						value={bySupplier.map((s, idx) => ({
							id: String(idx + 1),
							sno: String(idx + 1),
							supplier: s.supplier_name,
							cpv: utils.formatCost(s.total_cost ?? 0),
						}))}
						tableStyle={{
							width: "100% !important",
							maxHeight: "200px",
						}}
					>
						<Column field="sno" header="#"></Column>
						<Column field="supplier" header="Supplier"></Column>
						<Column field="cpv" header="CPV"></Column>
					</DataTable>
				</div>
			</div>
			{/* second card */}
			<div className="second-card">
				<div className="cards">
					<div className="chart" style={{ width: "100%" }}>
						<div className="title">
							CPV by Country (€)
							<span className="info-icon">
								<Tooltip target=".country-info-icon" />
								<i
									data-pr-tooltip="Average bid cost by supplier"
									data-pr-position="right"
									data-pr-at="right+5 top"
									data-pr-my="left center-2"
									className="country-info-icon pi pi-info-circle"
									style={{ fontSize: "1rem" }}
								></i>
							</span>
						</div>
						{/* Replace the placeholder waterfall with a donut built from live data */}
						<DonutChartJS
							data={countryDonut.data}
							options={countryDonut.options}
						/>
					</div>
				</div>
				<div className="cards">
					<div className="chart" style={{ width: "100%" }}>
						<div className="title">
							CPV by Year (€)
							<span className="info-icon">
								<Tooltip target=".year-info-icon" />
								<i
									data-pr-tooltip="CPV by year"
									data-pr-position="right"
									data-pr-at="right+5 top"
									data-pr-my="left center-2"
									className="year-info-icon pi pi-info-circle"
									style={{ fontSize: "1rem" }}
								></i>
							</span>
						</div>
						<BarWithAnnotation
							labels={yearLabels}
							values={yearValues}
							bracketBetween={
								yearLabels.length >= 2
									? ([
											yearLabels.length - 2,
											yearLabels.length - 1,
									  ] as [number, number])
									: undefined
							}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SpendAnalysis;
